import { I18nPluralPipe } from '@angular/common';
import { Component, computed, effect, inject, input, linkedSignal, model } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideEllipsisVertical, lucidePen, lucidePlus, lucideSearch, lucideTrash } from '@ng-icons/lucide';
import { LivrablePrincipal } from '@shared/models/livrable-principal.enum';
import { CanEditService } from '@shared/services/can-edit.service';
import { BrnSelectImports } from '@spartan-ng/brain/select';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmDropdownMenuImports } from '@spartan-ng/helm/dropdown-menu';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmIconImports } from '@spartan-ng/helm/icon';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmInputGroupImports } from '@spartan-ng/helm/input-group';
import { HlmItemImports } from '@spartan-ng/helm/item';
import { HlmSelectImports } from '@spartan-ng/helm/select';
import { HlmSeparatorImports } from '@spartan-ng/helm/separator';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';

import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { EstVerrouilleService } from '@shared/services/est-verrouille.service';
import { ReferentielStore } from '../../administration/gestion-référentiels/store/referentiel.store';
import { Cartographie } from '../../cartographie/models/cartographie.model';

import { AlerteComponent } from '../../shared/components/alerte/alerte.component';
import { ButtonAddNewComponent } from '../../shared/components/button-add-new/button-add-new.component';
import { ChipComponent } from '../../shared/components/chip/chip.component';
import {
  ExpansionPanel,
  ExpansionPanelMenuAction,
} from '../../shared/components/expansion-panel/expansion-panel.component';
import { SidePanelImports } from '../../shared/components/sidepanel';
import { SidepanelService } from '../../shared/components/sidepanel/sidepanel.service';
import { ToastrSuccessComponent } from '../../shared/components/toastr/toastr-success.component';
import { TrierParPipe } from '../../shared/pipes/trier-par.pipe';
import { Phase } from '../référentiel-métier-processus/processus-phases/models/phase.model';
import { PhaseStore } from '../référentiel-métier-processus/processus-phases/store/phase.store';
import { FormInterrelationsComponent } from './form-interrelations/form-interrelations.component';
import { Interrelation } from './models/interrelation.model';
import { InterrelationStore } from './store/interrelation.store';

@Component({
  selector: 'app-referentiel-metier-interrelations',
  imports: [
    HlmButtonImports,
    HlmDropdownMenuImports,
    HlmIconImports,
    HlmItemImports,
    HlmSeparatorImports,
    AlerteComponent,
    NgIcon,
    ButtonAddNewComponent,
    SidePanelImports,
    ReactiveFormsModule,
    HlmFieldImports,
    HlmSelectImports,
    BrnSelectImports,
    HlmInputImports,
    HlmCardImports,
    ExpansionPanel,
    TrierParPipe,
    HlmInputGroupImports,
    FormsModule,
    ChipComponent,
    I18nPluralPipe,
    EmptyStateComponent,
  ],
  templateUrl: './referentiel-metier-interrelations.component.html',
  providers: [provideIcons({ lucidePlus, lucideTrash, lucideSearch, lucidePen, lucideEllipsisVertical })],
})
export class ReferentielMetierInterrelationsComponent {
  readonly #interrelationStore = inject(InterrelationStore);
  readonly #phaseStore = inject(PhaseStore);
  readonly #referentielStore = inject(ReferentielStore);
  readonly #estVerrouilleService = inject(EstVerrouilleService);
  readonly #sidepanelService = inject(SidepanelService);
  readonly #canEditService = inject(CanEditService);

  nom = model<string | null>();

  protected readonly interrelationMapping: { [k: string]: string } = {
    '=0': '# interrelation',
    '=1': '# interrelation',
    other: '# interrelations',
  };

  readonly cartographie = input.required<Cartographie>();
  readonly roles = input.required<string[]>();
  readonly referentiel = computed(() => this.#referentielStore.selectedEntity());

  readonly phases = linkedSignal<Phase[]>(() => this.#phaseStore.entities());
  readonly interrelations = linkedSignal<Interrelation[]>(() => this.#interrelationStore.entities());

  readonly phasesFiltrees = linkedSignal<Phase[]>(this.phases);
  readonly peutModifier = computed(() =>
    this.#canEditService.check(
      this.referentiel()?.equipeResponsable,
      this.roles(),
      LivrablePrincipal.REFERENTIEL_METIER,
      this.cartographie(),
      this.referentiel(),
    ),
  );

  readonly estVerrouille = computed(() =>
    this.#estVerrouilleService.check(LivrablePrincipal.REFERENTIEL_METIER, this.cartographie(), this.referentiel()),
  );

  readonly areInterrelationsLinkedToPhases = computed(() => this.phases().some((x) => x.interrelations!.length > 0));
  readonly interrelationAction: ExpansionPanelMenuAction<Interrelation>[] = [
    {
      icon: 'lucideTrash',
      label: "Supprimer l'interrelation",
      action: (interrelation: Interrelation | undefined, phase: Phase | undefined) =>
        this.#delierInterrelation(interrelation, phase),
    },
  ];

  constructor() {
    effect(() => {
      const nom = this.nom()?.toLocaleLowerCase();
      if (nom) {
        this.phasesFiltrees.set(
          this.phases().filter((x) => x.interrelations?.some((x) => x.nom.toLocaleLowerCase().includes(nom))),
        );
      } else {
        this.phasesFiltrees.set(this.phases().filter((x) => x.interrelations!.length > 0));
      }
    });

    effect(() => {
      const referentiel = this.referentiel();
      if (!referentiel) return;

      this.#phaseStore.charger(referentiel.id);
      this.#interrelationStore.chargerToutes(this.cartographie().id);
    });
  }

  #delierInterrelation(interrelation?: Interrelation, phase?: Phase) {
    const referentiel = this.referentiel();
    if (!referentiel) return;

    if (!interrelation || !phase) return;

    const toastrId = uuidv7();
    const idsPhase = interrelation.idsPhase?.filter((id) => id !== phase.id);
    this.#interrelationStore.modifier({
      data: {
        interrelations: [
          {
            nom: interrelation.nom,
            idsPhase: idsPhase,
            id: interrelation.id,
          },
        ],
        idReferentiel: referentiel.id,
      },
      next: () => {
        this.#interrelationStore.chargerToutes(this.cartographie().id);
        this.#phaseStore.charger(referentiel.id);
        toast.custom(ToastrSuccessComponent, {
          componentProps: {
            message: 'Interrelation déliée',
            id: toastrId,
          },
          id: toastrId,
        });
      },
    });
  }

  openForm() {
    this.#sidepanelService.setComponentToDisplay(
      'Ajouter une interrelation',
      FormInterrelationsComponent,
      `Créez une ou plusieurs interrelations et liez-les à vos phases.`,
      {
        referentiel: this.referentiel(),
        cartographie: this.cartographie(),
      },
    );
    this.#sidepanelService.open();
  }
}
